/**
 * Git Integration Detail
 * 展示 Git 仓库集成的详细检测结果
 */

import React from 'react';
import {
  InfoCard,
  StatusOK,
  StatusWarning,
  StatusError,
  StructuredMetadataTable,
} from '@backstage/core-components';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { IntegrationCheckResult } from './types';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  scopeChip: {
    margin: theme.spacing(0.25),
    fontFamily: 'monospace',
    fontSize: '0.75rem',
  },
  tokenAlert: {
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  tokenValid: {
    background: 'rgba(76, 175, 80, 0.1)',
    border: '1px solid rgba(76, 175, 80, 0.3)',
  },
  tokenWarning: {
    background: 'rgba(255, 152, 0, 0.1)',
    border: '1px solid rgba(255, 152, 0, 0.3)',
  },
  tokenError: {
    background: 'rgba(244, 67, 54, 0.1)',
    border: '1px solid rgba(244, 67, 54, 0.3)',
  },
  providerRow: {
    '&:last-child td, &:last-child th': { border: 0 },
  },
}));

interface GitIntegrationDetailProps {
  result: IntegrationCheckResult;
}

const TOKEN_STATUS_MAP: Record<string, { label: string; color: string; icon: string }> = {
  valid: { label: 'Token 有效', color: '#4caf50', icon: '✅' },
  expiring_soon: { label: 'Token 即将过期', color: '#ff9800', icon: '⚠️' },
  expired: { label: 'Token 已过期', color: '#f44336', icon: '❌' },
  revoked: { label: 'Token 已吊销', color: '#f44336', icon: '🚫' },
  unknown: { label: 'Token 状态未知', color: '#999', icon: '❓' },
};

export const GitIntegrationDetail: React.FC<GitIntegrationDetailProps> = ({ result }) => {
  const classes = useStyles();

  // 基本信息
  const metadata: Record<string, string> = {
    '类型': 'GitLab',
    'Host': result.host,
    'Base URL': result.baseUrl,
    'API URL': result.apiBaseUrl,
    '检测时间': new Date(result.checkedAt).toLocaleString('zh-CN'),
  };

  if (result.gitlabVersion) {
    metadata['GitLab 版本'] = `${result.gitlabVersion.version} (${result.gitlabVersion.revision})`;
    if (result.gitlabVersion.edition) {
      metadata['版本类型'] = result.gitlabVersion.edition;
    }
  }

  // Token 状态
  const tokenStatusInfo = TOKEN_STATUS_MAP[result.tokenStatus] || TOKEN_STATUS_MAP.unknown;
  const tokenAlertClass = result.tokenStatus === 'valid' ? classes.tokenValid :
    result.tokenStatus === 'expiring_soon' ? classes.tokenWarning : classes.tokenError;

  return (
    <Box>
      {/* 基本信息 */}
      <InfoCard title="集成信息">
        <StructuredMetadataTable metadata={metadata} />
      </InfoCard>

      {/* Token 状态卡片 */}
      <Box mt={2}>
        <InfoCard title="Token 状态">
          {/* 状态告警条 */}
          <Box className={`${classes.tokenAlert} ${tokenAlertClass}`}>
            <Typography style={{ fontSize: '1.2rem' }}>{tokenStatusInfo.icon}</Typography>
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 600, color: tokenStatusInfo.color }}>
                {tokenStatusInfo.label}
              </Typography>
              {result.tokenDaysRemaining !== undefined && (
                <Typography variant="caption" color="textSecondary">
                  {result.tokenDaysRemaining > 0 
                    ? `剩余 ${result.tokenDaysRemaining} 天` 
                    : '已过期'}
                </Typography>
              )}
              {result.tokenDaysRemaining === undefined && result.tokenStatus === 'valid' && (
                <Typography variant="caption" color="textSecondary">
                  永不过期
                </Typography>
              )}
            </Box>
          </Box>

          {result.tokenInfo ? (
            <Box>
              {/* Token 详细信息 */}
              <Box className={classes.section}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                  Token 详情
                </Typography>
                <StructuredMetadataTable metadata={{
                  'Token 名称': result.tokenInfo.name,
                  'Token ID': String(result.tokenInfo.id),
                  'Token (脱敏)': result.tokenMasked,
                  '创建时间': new Date(result.tokenInfo.createdAt).toLocaleString('zh-CN'),
                  '最后使用': result.tokenInfo.lastUsedAt
                    ? new Date(result.tokenInfo.lastUsedAt).toLocaleString('zh-CN')
                    : '从未',
                  '过期时间': result.tokenInfo.expiresAt
                    ? new Date(result.tokenInfo.expiresAt).toLocaleString('zh-CN')
                    : '永不过期',
                  '状态': result.tokenInfo.active ? '活跃' : '停用',
                }} />
              </Box>

              <Divider style={{ marginBottom: 16 }} />

              {/* 用户信息 */}
              {result.tokenInfo.user && (
                <Box className={classes.section}>
                  <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                    Token 所有者
                  </Typography>
                  <StructuredMetadataTable metadata={{
                    '用户名': result.tokenInfo.user.username,
                    '姓名': result.tokenInfo.user.name,
                    '邮箱': result.tokenInfo.user.email,
                  }} />
                </Box>
              )}

              <Divider style={{ marginBottom: 16 }} />

              {/* 权限范围 */}
              <Box className={classes.section}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                  权限范围 (Scopes)
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                  {result.tokenInfo.scopes.map(scope => (
                    <Chip
                      key={scope}
                      label={scope}
                      size="small"
                      className={classes.scopeChip}
                      color={scope === 'api' ? 'primary' : 'default'}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box p={2} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                {result.connectionStatus === 'connected' 
                  ? '无法获取 Token 详细信息（可能 GitLab 版本 < 13.10 不支持 /personal_access_tokens/self）'
                  : '连接失败，无法获取 Token 信息'}
              </Typography>
            </Box>
          )}
        </InfoCard>
      </Box>

      {/* Catalog Provider 配置 */}
      {result.catalogProviders.length > 0 && (
        <Box mt={2}>
          <InfoCard title={`Catalog Provider 配置 (${result.catalogProviders.length})`}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider ID</TableCell>
                    <TableCell>类型</TableCell>
                    <TableCell>分支</TableCell>
                    <TableCell>项目/路径</TableCell>
                    <TableCell>Catalog 文件</TableCell>
                    <TableCell>同步频率</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.catalogProviders.map(provider => (
                    <TableRow key={`${provider.type}-${provider.providerId}`} className={classes.providerRow}>
                      <TableCell>
                        <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {provider.providerId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={provider.type === 'gitlab' ? '标准扫描' : '深层扫描'}
                          size="small"
                          color={provider.type === 'gitlab' ? 'primary' : 'secondary'}
                          style={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>{provider.branch || '-'}</TableCell>
                      <TableCell>
                        {provider.project ? (
                          <Box>
                            <Typography variant="caption" style={{ fontFamily: 'monospace' }}>
                              {provider.project}
                            </Typography>
                            {provider.scanPath && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                扫描: {provider.scanPath}
                              </Typography>
                            )}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" style={{ fontFamily: 'monospace' }}>
                          {provider.entityFilename || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {provider.schedule?.frequency || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfoCard>
        </Box>
      )}

      {/* 连接错误信息 */}
      {result.connectionStatus === 'error' && (
        <Box mt={2}>
          <InfoCard title="连接错误">
            <Box p={2} style={{ background: 'rgba(244, 67, 54, 0.05)', borderRadius: 4 }}>
              <StatusError>{result.connectionError || '无法连接到 GitLab 服务'}</StatusError>
            </Box>
          </InfoCard>
        </Box>
      )}

      {/* 概要统计 */}
      <Box mt={2} display="flex" style={{ gap: 16 }}>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          {result.connectionStatus === 'connected' ? (
            <StatusOK>连通性正常</StatusOK>
          ) : (
            <StatusError>连接失败</StatusError>
          )}
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          {result.tokenStatus === 'valid' && <StatusOK>Token 有效</StatusOK>}
          {result.tokenStatus === 'expiring_soon' && <StatusWarning>Token 即将过期</StatusWarning>}
          {(result.tokenStatus === 'expired' || result.tokenStatus === 'revoked') && <StatusError>Token 异常</StatusError>}
          {result.tokenStatus === 'unknown' && <StatusWarning>Token 未知</StatusWarning>}
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <StatusOK>{result.catalogProviders.length} 个 Catalog Provider</StatusOK>
        </Box>
      </Box>
    </Box>
  );
};
